/**
 * NotificationDrawer — unified slide-out panel combining:
 *   - Share invitations (👥)
 *   - Skill comments (💬)
 *   - AI suggestions (✨)
 *
 * Replaces the separate NotificationDropdown + SuggestionDrawer in the nav.
 * The bell button shows a combined count and turns blue when anything is pending.
 */

import { useState, useEffect, useRef, useCallback } from 'react';import React from 'react';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/data';
import { getCurrentUser } from 'aws-amplify/auth';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { useSuggestions } from '../hooks/useSuggestions';
import { SuggestionCard } from './SuggestionCard';
import SkillPlacementPickerDialog from './SkillPlacementPickerDialog';
import type { Schema } from "@backend-schema";

const client = generateClient<Schema>();
const publicClient = generateClient<Schema>({ authMode: 'apiKey' });

// ─── Types ────────────────────────────────────────────────────────────────────

type ShareNotification = {
  kind: 'share_invite' | 'share_comment';
  id: string;
  status: string;
  createdAt: string;
  owner: string;
  recipientEmail: string;
  senderName?: string;
  message?: string;
  commentCount?: number;
  skillId?: string;
  categoryId?: string;
  skillName?: string;
  commentId?: string;
  commentContent?: string;
  commentDate?: string;
  type?: string;
};

// ─── Bell button (exported so nav can use it standalone) ──────────────────────

interface NotificationBellProps {
  totalCount: number;
  onClick: () => void;
  isOpen: boolean;
}

export function NotificationBell({ totalCount, onClick, isOpen }: NotificationBellProps) {
  return (
    <button
      className={`nd-bell${totalCount > 0 ? ' nd-bell--active' : ''}`}
      onClick={onClick}
      aria-label={`Notifications${totalCount > 0 ? ` (${totalCount})` : ''}`}
      aria-expanded={isOpen}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20" aria-hidden="true">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      {totalCount > 0 && (
        <span className="nd-bell__badge" aria-hidden="true">
          {totalCount > 99 ? '99+' : totalCount}
        </span>
      )}
    </button>
  );
}

// ─── Main drawer ──────────────────────────────────────────────────────────────

interface NotificationDrawerProps {
  open: boolean;
  onClose: () => void;
  onCountChange?: (count: number) => void;
}

// ─── Suggestions section — only mounted when drawer is open ──────────────────

interface SuggestionsSectionProps {
  onCountChange: (count: number) => void;
}

function SuggestionsSection({ onCountChange }: SuggestionsSectionProps) {
  const [processingId, setProcessingId] = useState<string | null>(null);
  const {
    suggestions,
    loading: suggestionsLoading,
    acceptSuggestion,
    modifySuggestion,
    rejectSuggestion,
    placementResult,
    pendingSkillName,
    isSearching,
    searchStatus,
    allPackageSkills,
    dismissCreation,
    createPackageAndPlace,
    placeInExistingCategory,
  } = useSuggestions();

  const handleAccept = useCallback(async (id: string) => {
    setProcessingId(id);
    try {
      await acceptSuggestion(id);
    } finally {
      // Only clear if placement is complete (exact match) — for picker dialogs keep locked
      if (!isSearching) setProcessingId(null);
    }
  }, [acceptSuggestion, isSearching]);

  const handleDismiss = useCallback(() => {
    setProcessingId(null);
    dismissCreation();
  }, [dismissCreation]);

  const handlePlaceInCategory = useCallback(async (categoryId: string, packageId: string, packageName: string, categoryName: string) => {
    try {
      await placeInExistingCategory(categoryId, packageId, packageName, categoryName);
    } finally {
      setProcessingId(null);
    }
  }, [placeInExistingCategory]);

  const handleCreatePackage = useCallback(async (packageName: string, categoryName: string) => {
    try {
      await createPackageAndPlace(packageName, categoryName);
    } finally {
      setProcessingId(null);
    }
  }, [createPackageAndPlace]);

  useEffect(() => {
    onCountChange(suggestions.length);
  }, [suggestions.length, onCountChange]);

  if (suggestionsLoading) return null;
  if (suggestions.length === 0 && !isSearching) return null;

  return (
    <>
      {/* Placement search status is now shown inline in the card */}
      <section>
        <p style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', margin: '0 0 0.75rem' }}>
          AI Suggestions
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {suggestions.map((s) => (
            <SuggestionCard
              key={s.id}
              suggestion={s}
              onAccept={handleAccept}
              onModify={modifySuggestion}
              onReject={rejectSuggestion}
              globallyDisabled={processingId !== null && processingId !== s.id}
              processingStatus={processingId === s.id ? searchStatus : undefined}
            />
          ))}
        </div>
      </section>

      {/* Placement picker — shown for fuzzy, category, or no match */}
      {placementResult && placementResult.matchType !== 'exact' && (
        <SkillPlacementPickerDialog
          skillName={pendingSkillName}
          placementResult={placementResult}
          allPackageSkills={allPackageSkills}
          onPlaceInCategory={handlePlaceInCategory}
          onCreatePackage={handleCreatePackage}
          onDismiss={handleDismiss}
        />
      )}
    </>
  );
}

export function NotificationDrawer({ open, onClose, onCountChange }: NotificationDrawerProps) {
  const navigate = useNavigate();
  const drawerRef = useRef<HTMLDivElement>(null);
  const { isPro } = useSubscriptionContext();

  // Share notifications
  const [shareItems, setShareItems] = useState<ShareNotification[]>([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [suggestionCount, setSuggestionCount] = useState(0);

  const totalCount = shareItems.length + suggestionCount;

  // Notify parent of combined count
  useEffect(() => {
    onCountChange?.(totalCount);
  }, [totalCount, onCountChange]);

  // ── Fetch share notifications ──────────────────────────────────────────────

  const fetchShareNotifications = useCallback(async () => {
    setShareLoading(true);
    try {
      const user = await getCurrentUser();
      const userEmail = user.signInDetails?.loginId || user.username;

      const sentResult = await client.models.ShareInvitation.list();
      const receivedResult = await client.queries.getRecipientInvitations({
        recipientEmail: userEmail,
        recipientUsername: user.username,
      });

      interface RawInvitation {
        id: string;
        status: string;
        createdAt: string;
        owner?: string;
        recipientEmail?: string;
        senderName?: string;
        message?: string;
      }

      const received = (receivedResult.data?.invitations ?? []) as unknown as RawInvitation[];
      const sent = sentResult.data ?? [];

      // Pending invites
      const pendingInvites: ShareNotification[] = received
        .filter((inv) => inv?.status === 'pending')
        .map((inv) => ({
          kind: 'share_invite',
          id: inv.id,
          status: inv.status,
          createdAt: inv.createdAt,
          owner: inv.owner ?? '',
          recipientEmail: inv.recipientEmail ?? '',
          senderName: inv.senderName,
          message: inv.message,
        }));

      // Comments on received accepted invites
      const commentNotifs: ShareNotification[] = [];
      for (const inv of received.filter((i) => i?.status === 'accepted')) {
        if (!inv?.id) continue;
        try {
          const comments = await publicClient.models.ShareComment.list({
            filter: { shareInvitationId: { eq: inv.id } },
          });
          const count = comments.data.filter(
            (c) => c?.owner === inv.owner && c.owner !== user.username,
          ).length;
          if (count > 0) {
            commentNotifs.push({
              kind: 'share_comment',
              id: inv.id,
              status: inv.status as string,
              createdAt: inv.createdAt,
              owner: inv.owner ?? '',
              recipientEmail: inv.recipientEmail ?? '',
              commentCount: count,
              type: 'received',
            });
          }
        } catch { /* skip */ }
      }

      // Skill-level comments on sent accepted invites
      for (const inv of sent.filter((i) => i?.status === 'accepted')) {
        if (!inv?.id) continue;
        try {
          const comments = await publicClient.models.ShareComment.list({
            filter: { shareInvitationId: { eq: inv.id } },
          });
          const recipientComments = comments.data.filter(
            (c) => c?.owner !== user.username && c?.skillId,
          );
          for (const comment of recipientComments) {
            const skill = await client.models.Skills.get({ id: comment.skillId });
            commentNotifs.push({
              kind: 'share_comment',
              id: `${inv.id}-${comment.id}`,
              status: inv.status ?? '',
              createdAt: comment.createdAt ?? inv.createdAt,
              owner: inv.owner ?? '',
              recipientEmail: inv.recipientEmail ?? '',
              skillId: comment.skillId ?? undefined,
              categoryId: comment.categoryId ?? skill?.data?.categoryId ?? undefined,
              skillName: skill?.data?.name ?? 'Unknown Skill',
              commentId: comment.id ?? undefined,
              commentContent: comment.content ?? undefined,
              commentDate: comment.createdAt ?? undefined,
              type: 'sent',
            });
          }
        } catch { /* skip */ }
      }

      const all = [...pendingInvites, ...commentNotifs]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 10);

      setShareItems(all);
    } catch { /* ignore */ } finally {
      setShareLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShareNotifications();
  }, [fetchShareNotifications]);

  // Refresh when drawer opens
  useEffect(() => {
    if (open) fetchShareNotifications();
  }, [open, fetchShareNotifications]);

  // ── Keyboard / focus ───────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) drawerRef.current?.focus();
  }, [open]);

  // ── Share actions ──────────────────────────────────────────────────────────

  const handleAcceptInvite = async (id: string) => {
    try {
      const result = await client.mutations.acceptDeclineInvitation({
        invitationId: id,
        action: 'accepted',
      });
      if (result.data?.success) {
        setShareItems((prev) => prev.filter((i) => i.id !== id));
      }
    } catch { /* ignore */ }
  };

  const handleDeclineInvite = async (id: string) => {
    try {
      const result = await client.mutations.acceptDeclineInvitation({
        invitationId: id,
        action: 'declined',
      });
      if (result.data?.success) {
        setShareItems((prev) => prev.filter((i) => i.id !== id));
      }
    } catch { /* ignore */ }
  };

  const loading = shareLoading;
  const isEmpty = shareItems.length === 0 && suggestionCount === 0;

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          aria-hidden="true"
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.25)',
            zIndex: 1100,
          }}
        />
      )}

      {/* Panel */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Notifications"
        tabIndex={-1}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: '100%', maxWidth: '440px',
          background: 'white',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          zIndex: 1101,
          display: 'flex', flexDirection: 'column',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.25s ease',
          outline: 'none',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem',
          borderBottom: '1px solid #e5e7eb',
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#111827' }}>
              Notifications
            </h2>
            {totalCount > 0 && (
              <p style={{ margin: '0.125rem 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                {totalCount} item{totalCount !== 1 ? 's' : ''} need your attention
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close notifications"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '1.25rem', color: '#6b7280',
              padding: '0.25rem', borderRadius: '4px', lineHeight: 1,
            }}
          >✕</button>
        </div>

        {/* Body — only mount suggestion hook when open */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
          {loading && isEmpty && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[1, 2, 3].map((n) => (
                <div key={n} style={{
                  height: '5rem', borderRadius: '0.75rem',
                  background: '#f3f4f6',
                  animation: 'nd-pulse 1.5s ease-in-out infinite',
                }} />
              ))}
            </div>
          )}

          {!loading && isEmpty && (
            <div style={{
              borderRadius: '0.75rem', border: '1px solid #e5e7eb',
              background: '#f9fafb', padding: '2.5rem 1rem', textAlign: 'center',
            }}>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                You're all caught up — no notifications.
              </p>
            </div>
          )}

          {/* ── Share invitations & comments ── */}
          {shareItems.length > 0 && (
            <section style={{ marginBottom: suggestionCount > 0 ? '1.5rem' : 0 }}>
              <p style={{
                fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: '#9ca3af', margin: '0 0 0.75rem',
              }}>
                Sharing
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                {shareItems.map((item) => (
                  <ShareNotificationCard
                    key={item.id}
                    item={item}
                    isPro={isPro}
                    onAccept={handleAcceptInvite}
                    onDecline={handleDeclineInvite}
                    onNavigate={(path, state) => { onClose(); navigate(path, state); }}
                  />
                ))}
              </div>
            </section>
          )}

          {/* ── AI suggestions — only mounted when drawer is open ── */}
          {open && <SuggestionsSection onCountChange={setSuggestionCount} />}
        </div>
      </div>

      <style>{`
        @keyframes nd-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes nd-pulse-spin {
          to { transform: rotate(360deg); }
        }
        .nd-bell {
          position: relative;
          display: flex; align-items: center; justify-content: center;
          background: none; border: none; cursor: pointer;
          padding: 0.375rem; border-radius: 8px;
          color: #374151; transition: background 0.15s, color 0.15s;
        }
        .nd-bell:hover { background: #f3f4f6; }
        .nd-bell--active { color: #2563eb; }
        .nd-bell__badge {
          position: absolute; top: -2px; right: -4px;
          min-width: 1.1rem; height: 1.1rem;
          background: #2563eb; color: white;
          border-radius: 999px; font-size: 0.65rem; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
          padding: 0 0.2rem; border: 2px solid white;
          line-height: 1;
        }
      `}</style>
    </>
  );
}

// ─── Share notification card ──────────────────────────────────────────────────

interface ShareNotificationCardProps {
  item: ShareNotification;
  isPro: boolean;
  onAccept: (id: string) => void;
  onDecline: (id: string) => void;
  onNavigate: (path: string, state?: object) => void;
}

function ShareNotificationCard({ item, isPro, onAccept, onDecline, onNavigate }: ShareNotificationCardProps) {
  const isInvite = item.kind === 'share_invite';
  const isComment = item.kind === 'share_comment';

  const icon = isInvite
    ? (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ) : (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    );

  const cardStyle: React.CSSProperties = {
    borderRadius: '0.75rem',
    border: '1px solid #e5e7eb',
    background: 'white',
    padding: '0.875rem 1rem',
    display: 'flex', flexDirection: 'column', gap: '0.5rem',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
  };

  const iconWrapStyle: React.CSSProperties = {
    width: 28, height: 28, borderRadius: '50%',
    background: isInvite ? '#eff6ff' : '#f0fdf4',
    color: isInvite ? '#2563eb' : '#16a34a',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <div style={iconWrapStyle}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
            {isInvite
              ? (item.senderName || item.owner)
              : isComment && item.type === 'sent'
                ? `${item.recipientEmail} commented on ${item.skillName}`
                : `${item.commentCount} new comment${(item.commentCount ?? 0) > 1 ? 's' : ''} on shared skills`
            }
          </p>
          {isInvite && (
            <p style={{ margin: '0.125rem 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
              Wants to share their skills with you
            </p>
          )}
          {isComment && item.commentContent && (
            <p style={{ margin: '0.125rem 0 0', fontSize: '0.8rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.commentContent}
            </p>
          )}
        </div>
        <span style={{ fontSize: '0.7rem', color: '#9ca3af', flexShrink: 0 }}>
          {new Date(item.commentDate ?? item.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
        </span>
      </div>

      {isInvite && (
        <div style={{ display: 'flex', gap: '0.5rem', paddingLeft: '2.25rem' }}>
          <button
            onClick={() => isPro ? onAccept(item.id) : onNavigate('/settings/subscription')}
            style={{
              flex: 1, fontSize: '0.8rem', padding: '0.375rem 0',
              borderRadius: '0.5rem', border: 'none',
              background: '#2563eb', color: 'white',
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            Accept {!isPro && '🔒'}
          </button>
          <button
            onClick={() => onDecline(item.id)}
            style={{
              flex: 1, fontSize: '0.8rem', padding: '0.375rem 0',
              borderRadius: '0.5rem', border: '1px solid #e5e7eb',
              background: 'white', color: '#374151',
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            Decline
          </button>
        </div>
      )}

      {isComment && (
        <div style={{ paddingLeft: '2.25rem' }}>
          <button
            onClick={() => {
              if (item.categoryId) {
                onNavigate(`/app/skillpackages/categories/${item.categoryId}`, { state: { highlightSkillId: item.skillId } });
              } else {
                onNavigate('/app/shared-skills');
              }
            }}
            style={{
              fontSize: '0.8rem', padding: '0.375rem 0.875rem',
              borderRadius: '0.5rem', border: '1px solid #e5e7eb',
              background: 'white', color: '#374151',
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            View
          </button>
        </div>
      )}
    </div>
  );
}
